import cookie from 'cookie';
import Cookies from 'js-cookie';
import LocalForage from 'localforage';
import 'localforage-getitems';

import { STORE_NAMESPACE } from '@/store';
import { UserState } from '@/stores/user.store';
import { cookieSharedDomainConf, removeAccessTokenFromCookies, saveAccessTokenToCookies } from '@/helpers/user-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

type MutationType = { type: string; payload: any };

export const PersistStateLocalStoragePlugin = (storesToPersist: string[] = []) => {
	function isStoreToPersist(mutationType: string) {
		return storesToPersist.some(item => mutationType.includes(`${item}/`));
	}

	return function (store: any) {
		store.subscribe((mutation: MutationType, state: any) => {
			if (isStoreToPersist(mutation.type)) {
				const storeName = mutation.type.split('/')[0];

				// const trimmedState = getTrimmedState(storeName, state);
				LocalForage.setItem(storeName, state[storeName]);
			}
		});
	};
};

// we don't need to keep all user properties in sync with the cookies.
// that means they're not needed in the server for the initial render.
// note: cookie values may not exceed 4096 length.
const persistUserProperies = [
	'accessToken',
	'hideSeenDislikedTitles',
	'isPremium',
	'userRole',
	'jwId',
	'jwLoginId',
	'lastUsedLoginProvider',
	'location',
	'loggedInProviders',
	'onboardingStatus',
	'persistedOverriddenLanguage',
	'persistedWebLocale',
	'preferences',
	'settings', // TO-DO check why loadSettings is not setting it right
	'visibleFlyouts',
	'newBuyboxFilterType',
	'newBuyboxView',
	'newBuyboxTooltipAcknowledged',
	'loggedInNewBuyBoxView',
	'buyboxExpView',
	'accountCreationTime',
	'showOldTitlePage',
];

const filterUserProperties = (object: Partial<UserState>) =>
	Object.keys(object)
		.filter(key => persistUserProperies.includes(key))
		.reduce((root, key) => {
			root[key] = object[key as keyof UserState];
			return root;
		}, {} as any);

export const PersistStateCookiesPlugin = () => {
	return function (store: any) {
		store.subscribe((mutation: any, state: any) => {
			// WE ONLY PERSIST USER STORE TO COOKIES.
			if (mutation.type.includes('user/')) {
				const storeName = mutation.type.split('/')[0];

				const durationDay = 60 * 60 * 24;
				const duration = durationDay * 30 * 12 * 2; // two years
				const cookieExpiryDate = new Date(Date.now() + duration * 1000);

				// holds only the necessary properties that are needed for the initial server render
				const storeValue = filterUserProperties(state[storeName]);
				const { accessToken, ...userStoreValue } = storeValue;

				if (accessToken) {
					saveAccessTokenToCookies(accessToken, cookieExpiryDate);
				} else {
					removeAccessTokenFromCookies();
				}

				const stringifiedUserStoreValue = JSON.stringify(userStoreValue);

				// keep it temporarily
				const cookieValue = cookie.serialize(`${STORE_NAMESPACE}/${storeName}`, stringifiedUserStoreValue, {
					expires: cookieExpiryDate,
					path: '/',
				});
				document.cookie = cookieValue;

				const cookieName = `${STORE_NAMESPACE}_${storeName}`;
				Cookies.set(cookieName, stringifiedUserStoreValue, {
					expires: cookieExpiryDate,
					secure: true,
					sameSite: 'lax',
					/*
						Important : IF the path and domain are ever needed to be removed or changed
						we need to make sure that the old cookies are removed.
						Otherwise the browser will send duplicate cookies to the server.
					*/
					...cookieSharedDomainConf,
				});

				// Check if jwUser cookie exceeds 4KB limit
				cookieJwUserSizeExceedsLimitCheck(cookieName, stringifiedUserStoreValue);

				/*
					Because of adding domain and path to the jw_user cookie
					it is possible that old cookies without domain and path are still present.
					We need to remove them to avoid sending duplicate cookies to the server.
				*/
				if (detectDuplicateCookies(cookieName)) {
					Cookies.remove(cookieName);
				}
			}
		});
	};
};

// Detect duplicates by name using document.cookie
function detectDuplicateCookies(name: string) {
	if (!process.client) return false;

	const raw = document.cookie || '';
	const values = raw
		.split(/;\s*/) // split "a=1; b=2; a=3" → ["a=1","b=2","a=3"]
		.filter(s => s.startsWith(name + '='))
		.map(s => decodeURIComponent(s.slice(name.length + 1)));

	return values.length > 1;
}

let isCookieSizeErrorLogged = false;
function cookieJwUserSizeExceedsLimitCheck(name: string, value: string) {
	if (isCookieSizeErrorLogged) return;
	const maxCookieSize = 3584; // 3.5KB
	const cookieOverhead = `${name}=`.length; // account for the name and '='
	const cookieSize = cookieOverhead + new Blob([value]).size;
	if (cookieSize > maxCookieSize) {
		captureMessageForSentry(
			'[persist-state.plugin.ts] jw_user cookie exceeds 3.5KB limit',
			{
				cookieSize,
			},
			'error'
		);
		isCookieSizeErrorLogged = true;
	}
}
